// src/directives/tooltip.ts
import { DirectiveBinding, ObjectDirective } from 'vue';

interface TooltipOptions {
  text: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

const tooltip: ObjectDirective = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    // Default options
    const defaultPosition = 'top';

    // Parse options
    let tooltipText: string;
    let position: string = defaultPosition;

    if (typeof binding.value === 'string') {
      tooltipText = binding.value;
    } else if (binding.value && typeof binding.value === 'object') {
      const options = binding.value as TooltipOptions;
      tooltipText = options.text;
      position = options.position || defaultPosition;
    } else {
      // Use el.textContent as fallback
      tooltipText = el.textContent || '';
    }

    // Do nothing if no tooltip text
    if (!tooltipText) return;

    // Store tooltip data on the element so handlers always read the latest value
    (el as any)._tooltipText = tooltipText;
    (el as any)._tooltipPosition = position;

    // Set tooltip events - instant show/hide
    const handleMouseEnter = () => {
      showTooltip(el, (el as any)._tooltipText, (el as any)._tooltipPosition);
    };

    const handleMouseLeave = () => {
      hideTooltip();
    };

    const handleClick = () => {
      hideTooltip();
    };

    // Use passive listeners for better scroll performance
    el.addEventListener('mouseenter', handleMouseEnter, { passive: true });
    el.addEventListener('mouseleave', handleMouseLeave, { passive: true });
    el.addEventListener('click', handleClick);

    // Store event handlers for cleanup
    (el as any)._tooltipHandlers = {
      mouseenter: handleMouseEnter,
      mouseleave: handleMouseLeave,
      click: handleClick
    };

    // Set element style - only if not an input/textarea
    const isFormElement = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT';

    if (!isFormElement) {
      el.style.position = 'relative';
      el.style.cursor = 'help';
    }
  },

  updated(el: HTMLElement, binding: DirectiveBinding) {
    // Update tooltip text if changed
    let tooltipText: string;

    if (typeof binding.value === 'string') {
      tooltipText = binding.value;
    } else if (binding.value && typeof binding.value === 'object') {
      tooltipText = (binding.value as TooltipOptions).text;
    } else {
      tooltipText = el.textContent || '';
    }

    // Update stored tooltip text on the element
    (el as any)._tooltipText = tooltipText;

    // Update active tooltip if it exists
    const activeTooltip = document.getElementById('custom-tooltip');
    if (activeTooltip && activeTooltip.getAttribute('data-owner') === getUniqueId(el)) {
      activeTooltip.textContent = tooltipText;
    }
  },

  unmounted(el: HTMLElement) {
    // Clean up events using stored handlers
    const handlers = (el as any)._tooltipHandlers;
    if (handlers) {
      el.removeEventListener('mouseenter', handlers.mouseenter);
      el.removeEventListener('mouseleave', handlers.mouseleave);
      el.removeEventListener('click', handlers.click);
      delete (el as any)._tooltipHandlers;
    }

    // Remove tooltip if exists
    hideTooltip();
  }
};

// Get unique ID for element
function getUniqueId(el: HTMLElement): string {
  if (!el.dataset.tooltipId) {
    el.dataset.tooltipId = 'tooltip-' + Math.random().toString(36).substr(2, 9);
  }
  return el.dataset.tooltipId;
}

// Show tooltip
function showTooltip(el: HTMLElement, text: string, position: string) {
  // Remove any existing tooltips
  hideTooltip();

  // Create tooltip element with all styles inline for performance
  const tooltip = document.createElement('div');
  tooltip.id = 'custom-tooltip';
  tooltip.setAttribute('data-owner', getUniqueId(el));
  tooltip.textContent = text;

  // Apply CSS class instead of inline styles for better performance
  tooltip.className = 'custom-tooltip-base';

  // Only critical positioning styles inline
  tooltip.style.cssText = `
    position: fixed;
    background-color: rgba(17, 24, 39, 0.95);
    color: #fff;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 13px;
    line-height: 1.4;
    max-width: 320px;
    word-wrap: break-word;
    z-index: 99999;
    pointer-events: none;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    white-space: pre-wrap;
  `;

  // Add tooltip to document
  document.body.appendChild(tooltip);

  // Position tooltip
  const rect = el.getBoundingClientRect();

  const tooltipHeight = tooltip.offsetHeight;
  const tooltipWidth = tooltip.offsetWidth;

  // For input elements, prefer bottom position
  const isFormElement = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT';
  const preferredPosition = isFormElement ? 'bottom' : position;

  let top, left;

  switch (preferredPosition) {
    case 'bottom':
      top = rect.bottom + 8;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left - tooltipWidth - 8;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.right + 8;
      break;
    default: // top
      top = rect.top - tooltipHeight - 8;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
  }

  // Keep tooltip within viewport
  if (left < 10) left = 10;
  if (left + tooltipWidth > window.innerWidth - 10) {
    left = window.innerWidth - tooltipWidth - 10;
  }

  // Adjust vertical position if tooltip would go off screen
  if (top < 10) {
    // If tooltip would go above viewport, show it below the element instead
    top = rect.bottom + 8;
  } else if (top + tooltipHeight > window.innerHeight - 10) {
    // If tooltip would go below viewport, show it above the element instead
    top = rect.top - tooltipHeight - 8;
  }

  // Apply position - tooltip is immediately visible
  tooltip.style.top = `${top}px`;
  tooltip.style.left = `${left}px`;
}

// Hide tooltip - instant removal for better performance
function hideTooltip() {
  const tooltip = document.getElementById('custom-tooltip');
  if (tooltip && tooltip.parentNode) {
    tooltip.parentNode.removeChild(tooltip);
  }
}

export default tooltip;
